import { useMutationState } from '@tanstack/react-query'
import { queryKeys } from '../../../config/queryKeys'
import type { SlotRosterDetailsInput } from '../../../services/teamBalance'
import type { RosterDetailsSave } from './useTeamBalanceMutations'

/**
 * Inon, Oct 2 — every double-clicked Position / Number save still in flight on this
 * sheet, keyed by slot id: what each one is writing.
 *
 * Read from the MUTATION CACHE (TanStack's `useMutationState`), not from the
 * mutation's own result. A `useMutation` result only tracks its latest call, so a rep
 * who saves row A and then row B would see A stop looking busy the moment B started —
 * and A's row would take edits again before its own save had settled. Here each save
 * stays until IT settles; the mutation keeps itself pending until the sheet's refetch
 * lands (`onSuccess: invalidate`), so a row never flashes its old value in between.
 *
 * Two saves on one slot merge, the later field winning.
 */
export const useRosterDetailSaves = (
  teamId: string,
  rosterId: string
): Record<string, SlotRosterDetailsInput> => {
  'use memo'

  const saves = useMutationState({
    filters: {
      mutationKey: queryKeys.teamBalanceRosterDetails.save(teamId, rosterId),
      status: 'pending',
    },
    select: (mutation) =>
      mutation.state.variables as RosterDetailsSave | undefined,
  })

  const bySlot: Record<string, SlotRosterDetailsInput> = {}
  for (const save of saves) {
    if (save) {
      bySlot[save.slotId] = { ...bySlot[save.slotId], ...save.body }
    }
  }

  return bySlot
}

export default useRosterDetailSaves
